/**
 * Velinin haftalık davranış sayısı.
 *
 * Haftalık sayı gerçekte EVDEN geliyor: "bu hafta kaç kez kendi başına
 * ödevine başladı" sorusunu uygulayıcı seansta tahmin edemez. Bu ekran
 * davranış takibinin asıl giriş kapısı.
 *
 * EKRANDA PUAN, GRAFİK YA DA "İYİ GİDİYOR" YORUMU YOK. Veli raporunun
 * aynı kuralı: yorum uygulayıcıya ait. Burada velinin gördüğü tek şey
 * kendi girdiği sayılar ve hedef.
 *
 * Başka kaynakların (öğretmen, uygulayıcı) aynı hafta için girdiği
 * sayı da gösterilmiyor — sunucu zaten döndürmüyor. Görmek, tarafları
 * birbirinin cevabına göre ayarlamaya iter ve üç kaynaktan bağımsız
 * gözlem alma fikrini bozar.
 */

import { useState } from 'react'
import { api, type DavranisHedefi } from '../api'

const gun = (s: string) =>
  new Date(s + 'T00:00:00Z').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })

export function Davranislar({
  cocukId,
  hedefler,
  buHafta,
  onKaydedildi,
}: {
  cocukId: string
  hedefler: DavranisHedefi[]
  buHafta: string
  onKaydedildi: () => void
}) {
  if (hedefler.length === 0) return null

  return (
    <>
      <h2>Haftalık takip</h2>
      <p className="alt">
        {gun(buHafta)} haftası için soruyoruz. Tam sayı tutmanız gerekmiyor; yaklaşık bir sayı
        da işe yarıyor. Yanlış yazarsanız üstüne yeniden yazabilirsiniz.
      </p>
      {hedefler.map((h) => (
        <Hedef
          key={h.id}
          cocukId={cocukId}
          h={h}
          buHafta={buHafta}
          onKaydedildi={onKaydedildi}
        />
      ))}
    </>
  )
}

function Hedef({
  cocukId,
  h,
  buHafta,
  onKaydedildi,
}: {
  cocukId: string
  h: DavranisHedefi
  buHafta: string
  onKaydedildi: () => void
}) {
  const kayitli = h.kayitlar.find((k) => k.hafta === buHafta)
  const [deger, setDeger] = useState(kayitli ? String(kayitli.deger) : '')
  const [not, setNot] = useState(kayitli?.not ?? '')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)
  const [kaydedildi, setKaydedildi] = useState(false)

  const gecmis = h.kayitlar
    .filter((k) => k.hafta !== buHafta)
    .sort((a, b) => b.hafta.localeCompare(a.hafta))
    .slice(0, 6)

  const kaydet = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await api.davranisYaz(h.id, cocukId, buHafta, Number(deger), not.trim() || undefined)
      setKaydedildi(true)
      onKaydedildi()
    } catch {
      setHata('Kaydedilemedi. Bağlantınızı kontrol edip tekrar deneyin.')
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form className="kart" onSubmit={kaydet}>
      <div style={{ marginBottom: 8 }}>
        <strong>{h.metin}</strong>
        <p className="alt" style={{ margin: 0 }}>
          Birim: {h.birim}
          {h.hedef !== null ? ` · hedef ${h.hedef}` : ''}
        </p>
      </div>

      {hata && <div className="uyari">{hata}</div>}
      {kaydedildi && !hata && <div className="bilgi">Kaydedildi. Teşekkürler.</div>}

      <label>
        Bu hafta kaç?
        <input
          type="number"
          min={0}
          step="any"
          value={deger}
          onChange={(e) => {
            setDeger(e.target.value)
            setKaydedildi(false)
          }}
          required
        />
      </label>
      <label>
        Eklemek istediğiniz bir şey (isteğe bağlı)
        <input
          value={not}
          onChange={(e) => {
            setNot(e.target.value)
            setKaydedildi(false)
          }}
          maxLength={500}
          placeholder="Bu hafta hasta oldu, iki gün okula gitmedi."
        />
      </label>

      <button className="birincil" type="submit" disabled={bekliyor || deger === ''}>
        {bekliyor ? 'Kaydediliyor…' : kayitli ? 'Güncelle' : 'Kaydet'}
      </button>

      {gecmis.length > 0 && (
        <p className="alt" style={{ marginBottom: 0 }}>
          Daha önce girdikleriniz: {gecmis.map((k) => `${gun(k.hafta)}: ${k.deger}`).join(' · ')}
        </p>
      )}
    </form>
  )
}
